import Image from 'next/legacy/image'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

import styled from 'styled-components'

import { device } from '@/common/constants/device'

import Tabs from '@/components/common/Tabs'
import Text from '@/components/common/Text'
import Button from '@/components/ui/Button'
import WrapWithText from 'components/ui/WrapWithText'
import { HOME_PAGE } from '@/common/constants/analytics'
import { userAmplitudeAnalytics } from '@/common/services/userAmplitudeAnalytics'
import BaseLink from '@/components/common/BaseLink'

const tabsLabels = [
  { value: 'advertisers', label: 'advertisers' },
  { value: 'dropShippers', label: 'dropShippers' },
  { value: 'digitalMarketing', label: 'digitalMarketing' },
]

const tabsTitle = {
  advertisers: 'advertisers',
  dropShippers: 'dropShippers',
  digitalMarketing: 'digitalMarketing',
}

const tabsDescription = {
  advertisers: 'advertisersCan',
  dropShippers: 'dropShippersCan',
  digitalMarketing: 'digitalMarketingCan',
}

const tabsItems = {
  advertisers: ['advertisers1', 'advertisers2', 'advertisers3', 'advertisers4'],
  dropShippers: ['dropShippers1', 'dropShippers2', 'dropShippers3'],
  digitalMarketing: [
    'digitalMarketing1',
    'digitalMarketing2',
    'digitalMarketing3',
  ],
}

const Wrap = styled.section`
  padding: 2rem 0.5rem 0.75rem 0.5rem;

  @media ${device.tablet} {
    padding: 2rem 0;
  }

  @media ${device.laptop} {
    display: flex;
    align-items: center;
    gap: 1.125rem;
    padding-top: 5rem;
    padding-bottom: 5.625rem;
  }

  @media ${device.laptopL} {
    flex-direction: row-reverse;
    justify-content: center;
    gap: 6.25rem;

    padding-top: 4rem;
    padding-bottom: 4rem;
  }
`

const TextWrap = styled.div`
  @media ${device.laptop} {
    width: 485px;
  }

  @media ${device.laptopL} {
    width: 583px;
  }
`

const TabsWrap = styled.div`
  margin-top: 0.5rem;
  margin-bottom: 1rem;

  @media ${device.tablet} {
    margin-top: 1.25rem;
    margin-bottom: 1.5rem;
  }

  @media ${device.latpot} {
    margin-top: 0.75rem;
  }

  @media ${device.latpotL} {
    margin-top: 1.25rem;
  }
`

const Title = styled.h2`
  max-width: 775px;
  margin-bottom: 0.5rem;
  font-weight: 800;
  font-size: 1.625em;
  line-height: 1.125;
  letter-spacing: -0.02em;

  @media ${device.tablet} {
    margin-bottom: 1rem;
    font-size: 2.5em;
    max-width: 583px;
  }

  @media ${device.laptopL} {
    margin-bottom: 1.5rem;
  }
`

const Descr = styled(Text)`
  margin-bottom: 1rem;
  line-height: 1.67;

  @media ${device.tablet} {
    margin-bottom: 1.5rem;
    max-width: 490px;
  }
`

const List = styled.ul`
  margin-bottom: 16px;

  @media ${device.tablet} {
    margin-bottom: 24px;
  }

  @media ${device.laptopL} {
    margin-bottom: 32px;
  }
`

const Item = styled.li`
  display: flex;
  align-items: center;
  gap: 1rem;
  max-width: 360px;

  :not(:last-child) {
    margin-bottom: 12px;
  }

  @media ${device.tablet} {
    max-width: 530px;
  }

  @media ${device.laptop} {
    max-width: 470px;
  }

  span {
    flex-shrink: 0;
  }
`

const Btn = styled(Button)`
  padding: 1rem 2rem;
`

const ImageWrap = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: end;
  height: 426px;
  margin-top: 1.5rem;
  padding: 0.375rem;
  border-radius: 24px;
  background: url('/_next_static/landing/creativeIdeas/dominica-s.webp') center
    center/cover no-repeat;

  @media ${device.tablet} {
    margin-top: 2rem;
    padding: 2rem;
    height: 628px;
    background: url('/_next_static/landing/creativeIdeas/dominica-t.webp')
      center center/cover no-repeat;
  }

  @media ${device.laptop} {
    margin-top: 0;
    padding: 1rem;
    width: 480px;
    height: 594px;
    background: url('/_next_static/landing/creativeIdeas/dominica-l.webp')
      center center/cover no-repeat;
  }

  @media ${device.laptopL} {
    width: 507px;
    height: 628px;
    background: url('/_next_static/landing/creativeIdeas/dominica-ll.webp')
      center center/cover no-repeat;
  }
`

const Stats = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.6875rem 0.8125rem 0.6875rem 1.25rem;
  border-radius: 59px;
  background-color: var(--white);
`

const LikeAndComments = styled.div`
  display: flex;
  gap: 1.375rem;
`

const Likes = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`

const Comments = styled.div`
  display: none;
  gap: 0.5rem;
  align-items: center;

  @media ${device.tablet} {
    display: flex;
  }
`

const Img = styled.div`
  background-image: url(${({ src }) => src});
  background-repeat: no-repeat;
  background-position: center center;
  background-size: cover;
  width: 23px;
  height: 23px;
`

const CreativeIdeas = () => {
  const t = useTranslations('index')
  const [currentTabName, setCurrentTabName] = useState(tabsLabels[0].value)

  const tabsCallback = (label) => {
    setCurrentTabName(label)
  }

  return (
    <Wrap id="efficiency">
      <TextWrap>
        <WrapWithText textKey="forWho" bgColor="purple" color="purple" />
        <TabsWrap>
          <Tabs fileName="index" tabs={tabsLabels} cbFunc={tabsCallback} />
        </TabsWrap>
        <Title>{t(tabsTitle[currentTabName])}</Title>
        <Descr size="15" weight="500" sizeT="18">
          {t(tabsDescription[currentTabName])}
        </Descr>
        <List>
          {tabsItems[currentTabName].map((el) => (
            <Item key={el}>
              <Image
                src="/_next_static/landing/possibilities/fire.svg"
                alt="fire"
                width={24}
                height={24}
              />
              <Text color={'black'} size={'15'}>
                {t(el)}
              </Text>
            </Item>
          ))}
        </List>
        <BaseLink
          onClick={() =>
            userAmplitudeAnalytics.trackEvent(
              `${HOME_PAGE}_page_about_rates_clicked`
            )
          }
          href="/premium"
          isChangePath={false}
        >
          <Btn size="14" sizeT="18">
            {t('learnMoreAboutRates')}
          </Btn>
        </BaseLink>
      </TextWrap>
      <ImageWrap>
        <Stats>
          <WrapWithText text="@dominica" bgColor="purple" color="purple" />
          <LikeAndComments>
            <Likes>
              <Img src="/_next_static/icons/heart.svg" />
              <Text sizeT="20">1.2M</Text>
            </Likes>
            <Comments>
              <Img src="/_next_static/icons/message.svg" />
              <Text sizeT="20">45</Text>
            </Comments>
          </LikeAndComments>
        </Stats>
      </ImageWrap>
    </Wrap>
  )
}

export default CreativeIdeas
